/**
 * WizardStepEvaluation Component
 *
 * Step 3 of the presentation wizard: Processing and results display.
 * Shows upload progress, evaluation progress, and final results summary.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Alert,
    Chip,
    Divider
} from '@mui/material';
import {
    CheckCircle as CheckIcon,
    Visibility as ViewIcon,
    Close as CloseIcon,
    Refresh as RetryIcon
} from '@mui/icons-material';
import WizardProgressTracker from './WizardProgressTracker';
import { CONSOLIDATED_PIPELINE_STATUS } from '../../../utils/api/consolidatedPipelineApi';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebaseClient';

interface WizardStepEvaluationProps {
    presentationId: string | null;
    pipelineStatus: string | null;
    pipelineError: string | null;
    onViewReport: () => void;
    onClose: () => void;
    onRetry: () => void;
}

const WizardStepEvaluation: React.FC<WizardStepEvaluationProps> = ({
    presentationId,
    pipelineStatus,
    pipelineError,
    onViewReport,
    onClose,
    onRetry
}) => {
    const [presentation, setPresentation] = useState<any | null>(null);
    const [realtimeStatus, setRealtimeStatus] = useState(pipelineStatus);
    const [realtimeError, setRealtimeError] = useState(pipelineError);

    // Real-time status listener
    useEffect(() => {
        if (!presentationId) return;

        const unsubscribe = onSnapshot(
            doc(db, 'presentations', presentationId),
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data();
                    setPresentation({ id: snapshot.id, ...data });
                    setRealtimeStatus(data.status || data.evaluationStatus);
                    setRealtimeError(data.errorMessage);
                }
            },
            (error) => {
                console.error('Error listening to presentation updates:', error);
            }
        );

        return () => unsubscribe();
    }, [presentationId]);

    const currentStatus = realtimeStatus || pipelineStatus;
    const currentError = realtimeError || pipelineError;

    const isComplete = currentStatus === CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2;
    const isFailed = currentStatus === CONSOLIDATED_PIPELINE_STATUS.FAILED_V2;
    const isProcessing = !isComplete && !isFailed;

    // Score card component
    const ScoreCard = ({ title, score, color }: { title: string, score: number, color?: string }) => (
        <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="h3" color={color || 'primary.main'}>
                    {score !== undefined && score !== null ? score.toFixed(1) : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    out of 5.0
                </Typography>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header */}
            <Typography variant="h6" gutterBottom>
                {isComplete ? 'Evaluation Complete!' : isFailed ? 'Evaluation Failed' : 'Processing Evaluation'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                {isComplete
                    ? 'Your presentation has been evaluated successfully.'
                    : isFailed
                        ? 'An error occurred during evaluation.'
                        : 'Please wait while we analyze your presentation...'}
            </Typography>

            {/* Progress Tracker */}
            <WizardProgressTracker
                status={currentStatus}
                error={currentError}
            />

            {/* Results Summary (only show when complete) */}
            {isComplete && presentation && (
                <>
                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Evaluation Results
                        </Typography>
                    </Box>

                    {/* Score Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {/* Overall Score - Always shown */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <ScoreCard
                                title="Overall Score"
                                score={presentation.overallScore}
                                color="primary.main"
                            />
                        </Grid>

                        {/* Dynamic Category Score Cards */}
                        {(() => {
                            const categoryScores = presentation.categoryScores ||
                                presentation.finalReport?.category_scores || {};
                            const colors = ['success.main', 'info.main', 'warning.main', 'secondary.main', 'error.main'];

                            return Object.entries(categoryScores).map(([key, score], index) => {
                                // Convert key to display name (e.g., "value_proposition" -> "Value Proposition")
                                const displayName = key.split('_').map(word =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ');

                                return (
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
                                        <ScoreCard
                                            title={displayName}
                                            score={score as number}
                                            color={colors[index % colors.length]}
                                        />
                                    </Grid>
                                );
                            });
                        })()}
                    </Grid>

                    {/* Presentation Info */}
                    <Card variant="outlined" sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Presentation Details
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                {presentation.description || 'Untitled Presentation'}
                            </Typography>
                            {presentation.productName && (
                                <Chip
                                    label={`Product: ${presentation.productName}`}
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                            )}
                            {presentation.presentationType && (
                                <Chip
                                    label={presentation.presentationType}
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                            )}
                            {presentation.evaluationPipelineVersion && (
                                <Chip
                                    label="Fast Evaluation (V2)"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </CardContent>
                    </Card>

                    {/* Success Message */}
                    <Alert severity="success" icon={<CheckIcon />}>
                        Your presentation has been evaluated and the report is ready to view!
                    </Alert>
                </>
            )}

            {/* Failed State */}
            {isFailed && (
                <Alert severity="error" sx={{ mt: 3 }}>
                    <Typography variant="body2" gutterBottom>
                        <strong>Evaluation Failed:</strong> {currentError || 'An unknown error occurred'}
                    </Typography>
                    <Typography variant="caption">
                        You can try again or contact support if the problem persists.
                    </Typography>
                </Alert>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                {/* Left side buttons */}
                <Box>
                    {isFailed && onRetry && (
                        <Button
                            variant="outlined"
                            startIcon={<RetryIcon />}
                            onClick={onRetry}
                            color="primary"
                        >
                            Try Again
                        </Button>
                    )}
                </Box>

                {/* Right side buttons */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {isComplete && onViewReport && (
                        <Button
                            variant="contained"
                            startIcon={<ViewIcon />}
                            onClick={onViewReport}
                            color="primary"
                            size="large"
                        >
                            View Full Report
                        </Button>
                    )}
                    {(isComplete || isFailed) && onClose && (
                        <Button
                            variant={isComplete ? 'outlined' : 'contained'}
                            startIcon={<CloseIcon />}
                            onClick={onClose}
                            size="large"
                        >
                            Close
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Processing State Info */}
            {isProcessing && (
                <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                        This process typically takes 2-3 minutes. You can leave this page and return later - the evaluation will continue in the background.
                    </Typography>
                </Alert>
            )}
        </Box>
    );
};

export default WizardStepEvaluation;
